import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { leadEndpoints } from '@/lib/api/endpoints';
import { leadKeys } from '@/lib/queryKeys';
import type { AssignableUsersResponse } from '@/types/settings';

/**
 * Active users the current actor may assign leads to, scope-filtered on the
 * server (ALL → everyone, TEAM → own group, OWN → self). Backs the assignee
 * pickers so they only offer assignees the backend will accept — unlike
 * `useUsers`, which lists the whole roster regardless of assignment authority.
 *
 * `includeUserId` additionally surfaces a lead's current owner (resolved on the
 * server with the same membership scope) so an edit form can display who owns
 * the lead even when that user is outside the actor's assignable set — without
 * needing `users:view`.
 */
export function useAssignableUsers(options?: { enabled?: boolean; includeUserId?: string }) {
  const includeUserId = options?.includeUserId;
  return useApiQuery<AssignableUsersResponse>({
    queryKey: leadKeys.assignableUsers(includeUserId),
    queryFn: () =>
      apiGet<AssignableUsersResponse>(operationsClient, leadEndpoints.assignableUsers(), {
        params: includeUserId ? { includeUserId } : undefined,
      }),
    // Only fetch for actors who can actually use the assignee picker
    // (TEAM/ALL). OWN-scope reps never see it, so the query stays idle.
    enabled: options?.enabled,
    showErrorToast: false,
  });
}
